import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getReconOverview,
  getReconSupply,
  getReconReturn,
  getReconSupplySources,
  getReconSupplyDestinations,
  getReconSummaryExport,
} from "../../Services/banking/reconciliation";

const EXPORT_MIME_TYPES = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  pdf: "application/pdf",
};

const downloadBlob = (data, fmt) => {
  const url = window.URL.createObjectURL(
    new Blob([data], { type: EXPORT_MIME_TYPES[fmt] || "application/octet-stream" }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `reconciliation-summary.${fmt}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Helper for consistent error handling
const handleApiError = (err, rejectWithValue) => {
  return rejectWithValue(
    err.response?.data?.message || err.message || "Something went wrong",
  );
};

// discom_id is the logged-in discomId; state_id defaults to 6 in the service layer.

export const fetchReconOverview = createAsyncThunk(
  "reconciliation/fetchOverview",
  async ({ partyId, stateId }, { rejectWithValue }) => {
    try {
      const response = await getReconOverview(partyId, stateId);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchReconSupply = createAsyncThunk(
  "reconciliation/fetchSupply",
  async ({ partyId, groupBy, stateId }, { rejectWithValue }) => {
    try {
      const response = await getReconSupply(partyId, groupBy, stateId);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchReconReturn = createAsyncThunk(
  "reconciliation/fetchReturn",
  async ({ partyId, groupBy, stateId }, { rejectWithValue }) => {
    try {
      const response = await getReconReturn(partyId, groupBy, stateId);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchReconSupplySources = createAsyncThunk(
  "reconciliation/fetchSupplySources",
  async ({ partyId, stateId }, { rejectWithValue }) => {
    try {
      const response = await getReconSupplySources(partyId, stateId);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchReconSupplyDestinations = createAsyncThunk(
  "reconciliation/fetchSupplyDestinations",
  async ({ partyId, stateId }, { rejectWithValue }) => {
    try {
      const response = await getReconSupplyDestinations(partyId, stateId);
      return response?.data?.data || response?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const exportReconSummary = createAsyncThunk(
  "reconciliation/exportSummary",
  async ({ fmt = "xlsx" } = {}, { rejectWithValue }) => {
    try {
      const response = await getReconSummaryExport(fmt);
      downloadBlob(response.data, fmt);
      return true;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

const initialState = {
  // Data
  overview: null,
  supply: null,
  return: null,
  supplySources: null,
  supplyDestinations: null,

  // Per-section loading
  overviewLoading: false,
  supplyLoading: false,
  returnLoading: false,
  supplySourcesLoading: false,
  supplyDestinationsLoading: false,
  exportLoading: false,

  // Per-section error
  overviewError: null,
  supplyError: null,
  returnError: null,
  supplySourcesError: null,
  supplyDestinationsError: null,
  exportError: null,
};

const reconciliationSlice = createSlice({
  name: "reconciliation",
  initialState,
  reducers: {
    clearReconciliation: () => initialState,
  },
  extraReducers: (builder) => {
    // --- Overview ---
    builder
      .addCase(fetchReconOverview.pending, (state) => {
        state.overviewLoading = true;
        state.overviewError = null;
      })
      .addCase(fetchReconOverview.fulfilled, (state, action) => {
        state.overviewLoading = false;
        state.overview = action.payload;
      })
      .addCase(fetchReconOverview.rejected, (state, action) => {
        state.overviewLoading = false;
        state.overviewError = action.payload;
      });

    // --- Supply ---
    builder
      .addCase(fetchReconSupply.pending, (state) => {
        state.supplyLoading = true;
        state.supplyError = null;
      })
      .addCase(fetchReconSupply.fulfilled, (state, action) => {
        state.supplyLoading = false;
        state.supply = action.payload;
      })
      .addCase(fetchReconSupply.rejected, (state, action) => {
        state.supplyLoading = false;
        state.supplyError = action.payload;
      });

    // --- Return ---
    builder
      .addCase(fetchReconReturn.pending, (state) => {
        state.returnLoading = true;
        state.returnError = null;
      })
      .addCase(fetchReconReturn.fulfilled, (state, action) => {
        state.returnLoading = false;
        state.return = action.payload;
      })
      .addCase(fetchReconReturn.rejected, (state, action) => {
        state.returnLoading = false;
        state.returnError = action.payload;
      });

    // --- Supply Sources ---
    builder
      .addCase(fetchReconSupplySources.pending, (state) => {
        state.supplySourcesLoading = true;
        state.supplySourcesError = null;
      })
      .addCase(fetchReconSupplySources.fulfilled, (state, action) => {
        state.supplySourcesLoading = false;
        state.supplySources = action.payload;
      })
      .addCase(fetchReconSupplySources.rejected, (state, action) => {
        state.supplySourcesLoading = false;
        state.supplySourcesError = action.payload;
      });

    // --- Supply Destinations ---
    builder
      .addCase(fetchReconSupplyDestinations.pending, (state) => {
        state.supplyDestinationsLoading = true;
        state.supplyDestinationsError = null;
      })
      .addCase(fetchReconSupplyDestinations.fulfilled, (state, action) => {
        state.supplyDestinationsLoading = false;
        state.supplyDestinations = action.payload;
      })
      .addCase(fetchReconSupplyDestinations.rejected, (state, action) => {
        state.supplyDestinationsLoading = false;
        state.supplyDestinationsError = action.payload;
      });

    // --- Export ---
    builder
      .addCase(exportReconSummary.pending, (state) => {
        state.exportLoading = true;
        state.exportError = null;
      })
      .addCase(exportReconSummary.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(exportReconSummary.rejected, (state, action) => {
        state.exportLoading = false;
        state.exportError = action.payload;
      });
  },
});

export const { clearReconciliation } = reconciliationSlice.actions;

export default reconciliationSlice.reducer;
